import { inject, injectable } from "inversify";
import type * as THREE from "three";
import { EngineConfigService, Logger, type PointerPosition } from "@designboticteam/core";
import {
	CursorSnappingService,
	PolygonTool,
	type Polyline,
	SceneService,
	type Shape,
	ShapeEditingService,
	Workspace3DStoreService,
} from "@designboticteam/workspace-3d";
import { Kolega3DStoreService, type ParcelSelectionMode, type ZoneToolMode } from "../../services/Kolega3DStoreService";
import { ParcelPickerService } from "../../services/ParcelPickerService";
import { createZoneStyleByType, type ZoneStyleByType } from "./constants";
import { ParcelSelection } from "./ParcelSelection";
import type { ZoneType } from "./types";
import { Zone } from "./Zone";

type ZoneToolOptions = {
	/** A newly closed check zone replaces the previous one. */
	singleCheckZone?: boolean;
	/** A closed zone is selected for editing straight away, until startDrawing() is called. */
	editAfterAccept?: boolean;
};

/**
 * Creates zones in one of three modes (see ZoneToolMode in the store): `drawing` sketches a polygon,
 * `picking` collects cadastral parcels whose union becomes the zone(s), `editing` forwards the pointer
 * to ShapeEditingService for the selected zone.
 */
@injectable()
class ZoneTool extends PolygonTool {
	public static readonly toolName: string = "ZoneTool";
	public override readonly toolName: string = ZoneTool.toolName;
	protected override readonly logger = new Logger(`🟨 ${this.toolName}`, {
		color: "#ffffff",
		backgroundColor: "#ca8a04",
	});

	private readonly zones = new Set<Zone>();
	private readonly parcelSelection: ParcelSelection;
	private draftZoneType: ZoneType = "CheckZone";
	private options: ZoneToolOptions = {};
	private isParcelPickInFlight = false;

	constructor(
		@inject(ShapeEditingService) protected readonly shapeEditingService: ShapeEditingService,
		@inject(SceneService) protected readonly sceneService: SceneService,
		@inject(CursorSnappingService) protected readonly cursorSnappingService: CursorSnappingService,
		@inject(Workspace3DStoreService) protected readonly workspace3DStoreService: Workspace3DStoreService,
		@inject(Kolega3DStoreService) private readonly kolega3DStoreService: Kolega3DStoreService,
		@inject(EngineConfigService) private readonly engineConfigService: EngineConfigService,
		@inject(ParcelPickerService) private readonly parcelPickerService: ParcelPickerService,
	) {
		super(shapeEditingService, sceneService, cursorSnappingService, workspace3DStoreService);

		this.parcelSelection = new ParcelSelection({
			scene: sceneService.scene,
			palette: engineConfigService.palette,
			ringToScenePoints: (ring) => parcelPickerService.ringToScenePoints(ring),
		});
	}

	public configure(options: ZoneToolOptions): void {
		this.options = options;
	}

	private get zoneStyleByType(): ZoneStyleByType {
		return createZoneStyleByType(this.engineConfigService.palette);
	}

	public setDraftZoneType = (zoneType: ZoneType): void => {
		if (this.draftZoneType === zoneType) {
			return;
		}

		this.draftZoneType = zoneType;

		if (this.currentShape instanceof Zone) {
			this.currentShape.setZoneType({ zoneType });
		}
	};

	public getDraftZoneType = (): ZoneType => this.draftZoneType;

	public changeSelectedZoneType = (zoneType: ZoneType): void => {
		const zone = this.getSelectedZone();

		if (zone === null) {
			return;
		}

		zone.setZoneType({ zoneType });
		this.kolega3DStoreService.store.setState({ isValidationDirty: true });
	};

	/** */
	public startEditing(zone: Zone): void {
		this.leavePicking();
		this.enterEditingMode({ shape: zone });
	}

	/** Leaves editing or picking and lets the user sketch the next zone. */
	public startDrawing(): void {
		if (this.getMode() === "drawing") {
			return;
		}

		this.leavePicking();
		this.shapeEditingService.selectShape(null);
		this.setMode("drawing");
	}

	/** Leaves drawing or editing and lets the user pick parcels; the zone is built from their union. */
	public startPicking(): void {
		if (this.getMode() === "picking") {
			return;
		}

		this.discardCurrentShape();
		this.shapeEditingService.selectShape(null);
		this.setMode("picking");
		// Picking clicks whole parcels; snap indicators would only suggest a precision that plays no part.
		this.cursorSnappingService.setContextActive(false);
	}

	public setParcelSelectionMode(mode: ParcelSelectionMode): void {
		this.parcelSelection.setMode(mode);
		this.syncParcelSelectionStore();
	}

	public removeSelectedParcel(id: string): void {
		if (this.parcelSelection.remove(id) === false) {
			this.logger.log(`Deselecting parcel ${id} would split the selection — rejected`);
			return;
		}

		this.syncParcelSelectionStore();
	}

	public clearParcelSelection(): void {
		this.parcelSelection.clear();
		this.syncParcelSelectionStore();
	}

	/** One zone per connected part of the picked parcels; the selection is consumed. */
	public createZonesFromParcelSelection({ zoneType }: { zoneType: ZoneType }): void {
		for (const points of this.parcelSelection.getMergedOutlines()) {
			if (points.length >= 3) {
				this.createZoneFromPoints({ zoneType, points });
			}
		}

		this.clearParcelSelection();
	}

	/** */
	public createZoneFromPoints({ zoneType, points }: { zoneType: ZoneType; points: THREE.Vector3[] }): Zone {
		const zone = new Zone({ zoneType, zoneStyleByType: this.zoneStyleByType });
		zone.setPoints({ points });

		this.sceneService.scene.add(zone.sceneObject);
		this.shapeEditingService.registerShape(zone);
		this.shapes.push(zone);

		zone.finish();
		this.applyLabelsVisibility();

		// A Line2 only offers snap candidates through a registered source — the draw flow does this in
		// PolylineTool.onTap, but zones built from parcels never go through it.
		this.cursorSnappingService.registerSnapSource(zone.line, () => zone.getPoints());

		this.addZone(zone);

		return zone;
	}

	/** */
	public cloneSelectedZoneWithOffset = (offset: number): void => {
		const zone = this.getSelectedZone();

		if (zone === null || Number.isFinite(offset) === false) {
			return;
		}

		const clone = this.cloneZone({ sourceZone: zone, zoneType: zone.getZoneType(), offset });

		if (clone !== null) {
			this.shapeEditingService.selectShape(clone);
		}
	};

	/** Null when the offset swallows the whole ring. */
	public createOffsetZone({ sourceZone, zoneType, offset }: { sourceZone: Zone; zoneType: ZoneType; offset: number }): Zone | null {
		return this.cloneZone({ sourceZone, zoneType, offset });
	}

	private cloneZone({ sourceZone, zoneType, offset }: { sourceZone: Zone; zoneType: ZoneType; offset: number }): Zone | null {
		const clone = new Zone({ zoneType, zoneStyleByType: this.zoneStyleByType });
		clone.setPoints({ points: sourceZone.getPoints() });

		// One-shot on a freshly built ring, so Polygon's inherited offset is enough — and unlike the
		// old Zone-local clipper call it normalises winding, so the sign means the same thing
		// whichever way the zone was drawn.
		if (offset !== 0 && clone.offset({ distance: offset }) === false) {
			// An inset deeper than the shape leaves nothing — better no zone than a copy of the source.
			clone.dispose();
			this.logger.log(`Offset ${offset} collapses the zone — rejected`);
			return null;
		}

		this.sceneService.scene.add(clone.sceneObject);
		this.shapeEditingService.registerShape(clone);
		this.shapes.push(clone);
		this.applyLabelsVisibility();
		this.cursorSnappingService.registerSnapSource(clone.line, () => clone.getPoints());

		this.addZone(clone);

		return clone;
	}

	public getZones(): ReadonlyArray<Zone> {
		return [...this.zones];
	}

	public getZoneType(shape: Shape): ZoneType | null {
		return this.findZone(shape)?.getZoneType() ?? null;
	}

	public clearAll(): void {
		for (const zone of [...this.zones]) {
			this.removeZone(zone);
		}

		this.syncStore();
	}

	/** */
	public removeZonesOfType(zoneType: ZoneType): void {
		for (const zone of [...this.zones]) {
			if (zone.getZoneType() === zoneType) {
				this.removeZone(zone);
			}
		}

		this.syncStore();
	}

	public deleteShape(shape: Shape): boolean {
		const zone = this.findZone(shape);

		if (zone === undefined) {
			return false;
		}

		this.removeZone(zone);
		this.syncStore();
		return true;
	}

	/** Esc: in picking the parcel selection is the sketch being cancelled. */
	public override cancelDrawing(): void {
		if (this.isPicking()) {
			this.clearParcelSelection();
			return;
		}

		super.cancelDrawing();
	}

	public override onDeactivate(): void {
		super.onDeactivate();
		this.leavePicking();
		this.shapeEditingService.selectShape(null);
		this.setMode("drawing");
	}

	public override onTap(position: PointerPosition): void {
		if (this.isEditing()) {
			this.shapeEditingService.onTap(position);
			return;
		}

		if (this.isPicking()) {
			void this.toggleParcelAt(position);
			return;
		}

		super.onTap(position);
	}

	public override onDoubleTap(position: PointerPosition): void {
		if (this.isEditing() || this.isPicking()) {
			return;
		}

		super.onDoubleTap(position);
	}

	public override onPointerMove(position: PointerPosition): void {
		if (this.isEditing()) {
			this.shapeEditingService.onPointerMove(position);
			return;
		}

		if (this.isPicking()) {
			return;
		}

		super.onPointerMove(position);
	}

	public onPointerDown(position: PointerPosition): void {
		if (this.isEditing()) {
			this.shapeEditingService.onPointerDown(position);
		}
	}

	public onDragStart(position: PointerPosition): void {
		if (this.isEditing()) {
			this.shapeEditingService.onDragStart(position);
		}
	}

	public onDragMove(position: PointerPosition): void {
		if (this.isEditing()) {
			this.shapeEditingService.onDragMove(position);
		}
	}

	public onDragEnd(position: PointerPosition): void {
		if (this.isEditing()) {
			this.shapeEditingService.onDragEnd(position);
		}
	}

	protected override createShape(): Polyline {
		return new Zone({
			zoneType: this.draftZoneType,
			zoneStyleByType: this.zoneStyleByType,
		});
	}

	protected override onShapeAccepted(shape: Polyline): void {
		super.onShapeAccepted(shape);

		if (shape instanceof Zone) {
			this.addZone(shape);
		}
	}

	protected override acceptCurrentShape(): void {
		const acceptedShape = this.currentShape;
		super.acceptCurrentShape();

		if (acceptedShape === null || this.options.editAfterAccept !== true) {
			return;
		}

		// Only safe after super has released currentShape — anything that deactivates this tool
		// while it is still set would dispose the shape we just accepted.
		this.enterEditingMode({ shape: acceptedShape });
	}

	private enterEditingMode({ shape }: { shape: Polyline }): void {
		this.setMode("editing");
		this.shapeEditingService.selectShape(shape);
	}

	/** The parcel selection is the picking-mode sketch: leaving the mode throws it away. */
	private leavePicking(): void {
		if (this.isPicking() === false) {
			return;
		}

		this.clearParcelSelection();
		this.cursorSnappingService.setContextActive(true);
	}

	private async toggleParcelAt(position: PointerPosition): Promise<void> {
		// The FlatGeobuf fetch is async; a second tap before it resolves would race the first.
		if (this.isParcelPickInFlight) {
			return;
		}

		this.isParcelPickInFlight = true;

		try {
			const parcel = await this.parcelPickerService.pickParcelAt(position);

			if (parcel === null) {
				this.logger.log("No parcel at the tapped position.");
				return;
			}

			// The mode may have changed while the fetch was in flight.
			if (this.isPicking() === false) {
				return;
			}

			if (this.parcelSelection.toggle(parcel) === false) {
				this.logger.log(`Parcel ${parcel.id} is not adjacent to the selection — rejected`);
				return;
			}

			this.syncParcelSelectionStore();
		} finally {
			this.isParcelPickInFlight = false;
		}
	}

	private syncParcelSelectionStore(): void {
		this.kolega3DStoreService.store.setState({
			parcelSelection: { mode: this.parcelSelection.getMode(), items: this.parcelSelection.getItems() },
		});
	}

	private addZone(zone: Zone): void {
		if (this.options.singleCheckZone === true && zone.getZoneType() === "CheckZone") {
			for (const existing of [...this.zones]) {
				if (existing.getZoneType() === "CheckZone") {
					this.removeZone(existing);
				}
			}
		}

		this.zones.add(zone);
		this.syncStore();
	}

	private removeZone(zone: Zone): void {
		this.shapeEditingService.unregisterShape(zone);
		zone.dispose();
		this.forgetShape(zone);
		this.zones.delete(zone);
	}

	private syncStore(): void {
		this.kolega3DStoreService.store.setState({ zones: [...this.zones], isValidationDirty: true });
	}

	private findZone(shape: Shape | null): Zone | undefined {
		return shape instanceof Zone && this.zones.has(shape) ? shape : undefined;
	}

	private getSelectedZone(): Zone | null {
		return this.findZone(this.workspace3DStoreService.store.getState().tools.selectedShape) ?? null;
	}

	private setMode(mode: ZoneToolMode): void {
		this.kolega3DStoreService.store.setState({ zoneToolMode: mode });
	}

	private getMode(): ZoneToolMode {
		return this.kolega3DStoreService.store.getState().zoneToolMode;
	}

	private isEditing(): boolean {
		return this.getMode() === "editing";
	}

	private isPicking(): boolean {
		return this.getMode() === "picking";
	}
}

export { ZoneTool, type ZoneToolOptions };
